import type {Element, Root} from 'hast'
import {visit} from 'unist-util-visit'

import {getTextContent, isTag} from '../hast-utils'

// TODO: The URL filter should match video_asset_allowlist: https://github.com/github/github/blob/499d51f01c455b05f5a09aa6fd7827353df10a3d/lib/github/config.rb#L6036
// which would allow for GHES/Proxima/CDN-hosted videos. We can't just copy the list from Rails because it's dynamic,
// so we'll probably need to add a new API.

const GITHUB_ATTACHMENT_HOSTS = new Set(['github.com', 'www.github.com', 'gist.github.com', 'github.localhost'])
const GITHUB_ATTACHMENT_PATH_PREFIX = '/user-attachments/assets/'

function tryParseUrl(href: string): URL | undefined {
  try {
    const origin =
      typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'https://github.com'
    return new URL(href, origin)
  } catch {
    return undefined
  }
}

// Returns true when the provided href points directly to a video asset we can embed.
export function isRenderableVideoUrl(href: unknown): href is string {
  if (typeof href !== 'string') return false
  const trimmed = href.trim()
  if (!trimmed) return false

  const parsed = tryParseUrl(trimmed)
  if (
    parsed &&
    GITHUB_ATTACHMENT_HOSTS.has(parsed.hostname) &&
    parsed.pathname.startsWith(GITHUB_ATTACHMENT_PATH_PREFIX)
  ) {
    return true
  }
  return false
}

// Replaces anchor tags that link to raw videos with <video> nodes that play inline.
export function convertVideoLinks(tree: Root): void {
  visit(tree, 'element', (node, index, parent) => {
    if (!parent || typeof index !== 'number') return
    if (!isTag(node, 'p')) return

    // Video links must be the only meaningful child of a paragraph (allow whitespace-only text nodes)
    const meaningfulChildren = node.children.filter(
      c => c.type === 'element' || (c.type === 'text' && c.value.trim() !== ''),
    )
    if (meaningfulChildren.length !== 1) return
    const child = meaningfulChildren[0]
    if (child?.type !== 'element' || !isTag(child, 'a')) return

    const href = child.properties?.href
    if (!isRenderableVideoUrl(href)) return

    const textContent = getTextContent(child)
    const labelSource = typeof href === 'string' ? href.split('#', 1)[0]?.split('?', 1)[0] ?? href : ''
    const ariaLabel = textContent || labelSource || 'Video player'
    const videoNode: Element = {
      type: 'element',
      tagName: 'video',
      properties: {
        src: href,
        controls: true,
        preload: 'metadata',
        playsInline: true,
        'aria-label': ariaLabel,
      },
      children: [],
    }

    if (node.position) {
      videoNode.position = {
        start: {...node.position.start},
        end: {...node.position.end},
      }
    }

    parent.children[index] = videoNode
  })
}
